import { RawInput } from '@ps/web-shared-components';
import classNames from 'clsx';
import { flatten } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { CMSHeading } from '@swf/cms';
import CloseButton, { Variant } from '@swf/components/StarsModal/components/CloseButton';
import ArrowIcon from '@swf/core/assets/icons/arrow.svg';
import { fetchJson } from '@swf/core/utils/api';

// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../../Events.scss';

export interface SeatDrawInfo {
  onClose: () => void;
  seatDrawUrl: string;
}

export interface SeatDraw {
  name: string;
  table: number;
  seat: string;
  country: string;
  chips: number;
}

interface SeatDrawData {
  list: [
    {
      id: number;
      seats: [
        {
          id: number;
          player: {
            player: string;
            seat: string;
            country: string;
            chips: string;
          };
        }
      ];
    }
  ];
}

const extractSeatDraw = (data: SeatDrawData) =>
  flatten(
    data?.list?.map(
      ({ seats, id }) =>
        seats.map((item) => ({
          name: item?.player?.player,
          table: id,
          seat: item?.id,
          country: item?.player?.country,
          chips: item?.player?.chips ? parseInt(item.player.chips) : ''
        })) as unknown as SeatDraw[]
    )
  );

const tableHeader = [
  { text: 'Name', prop: 'name' },
  { text: 'Country', prop: 'country' },
  { text: 'Table - Seat', prop: 'table' },
  { text: 'Chips', prop: 'chips' }
];

const SeatDrawModal: React.FC<SeatDrawInfo> = ({ onClose, seatDrawUrl }) => {
  const [seatDraw, setSeatDraw] = useState<SeatDraw[]>([]);
  const [filteredSeatDraw, setFilteredSeatDraw] = useState<SeatDraw[]>([]);
  const [nameFilter, setNameFilter] = useState('');
  const [sortFilter, setSortFilter] = useState({ prop: 'name', isDes: false });

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchData = async () => {
      try {
        const data = await fetchJson<SeatDrawData>(seatDrawUrl, { signal });
        setSeatDraw(extractSeatDraw(data));
      } catch {
        setSeatDraw([]);
      }
    };

    fetchData();

    return () => abortController.abort();
  }, [seatDrawUrl]);

  useEffect(() => {
    let result = [...seatDraw];
    if (nameFilter) {
      result = result.filter((a) => a.name?.toLowerCase().includes(nameFilter.toLowerCase()));
    }

    if (!sortFilter.isDes) {
      // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
      result = result.sort((a, b) => (a[sortFilter.prop] < b[sortFilter.prop] ? -1 : 1));
    } else {
      // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
      result = result.sort((a, b) => (b[sortFilter.prop] < a[sortFilter.prop] ? -1 : 1));
    }

    setFilteredSeatDraw(result);
  }, [nameFilter, seatDraw, sortFilter, sortFilter.prop, sortFilter.isDes]);

  const onFilter = (prop: string) => {
    let currentValue = !sortFilter.isDes;
    if (sortFilter.prop !== prop) {
      currentValue = false;
    }

    setSortFilter({ prop, isDes: currentValue });
  };

  return (
    <div className={styles.eventInfo}>
      <CMSHeading title="Seat draw" level="h3" size="l" hero={false} />
      <CloseButton variant={Variant.Dark} onClick={onClose} />
      <div className={styles.tableContainer}>
        <div className={styles.spacedBlock}>
          <RawInput
            label="Player Name"
            id="player-name-filter"
            value={nameFilter}
            onChange={({ target }) => setNameFilter(target.value)}
          />
        </div>
        <table className={styles.eventsTable}>
          <thead>
            <tr>
              {tableHeader.map(({ text, prop }) => (
                <th key={prop} onClick={() => onFilter(prop)} data-testid={prop}>
                  <span>{text}</span>{' '}
                  <ArrowIcon
                    className={classNames(styles.sort, {
                      [styles.sortAsc]: sortFilter.prop === prop && !sortFilter.isDes
                    })}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSeatDraw.map((item, index) => (
              <tr key={index} data-testid="seat-draws">
                <td>{item.name}</td>
                <td>{item.country}</td>
                <td>
                  {item.table} - {item.seat}
                </td>
                <td>{item.chips && <FormattedNumber style="decimal" value={item.chips} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeatDrawModal;
